import { Suspense, lazy } from "react";
import { CustomProvider } from "rsuite";
import Providers from "./layouts/provider.layout";
import { Loader } from "./components";
import ErrorBoundary from "./ErrorBoundary";
import { ThemeProvider } from "./contexts/ThemeContext";
import * as Sentry from "@sentry/react";
import './assets/rsuite-theme.less';
import config from "./config";

const PublicEnrolmentApp = lazy(() => import("./public-enrolment/PublicEnrolmentApp"));
const PublicWaitlistApp = lazy(() => import("./public-enrolment/PublicWaitlistApp"));

const isPublicWaitlist = /^\/waitlist\/?$/.test(window.location.pathname);
const isPublicEnrolment = /^\/enrolment\/?$/.test(window.location.pathname);
const isPublic = isPublicEnrolment || isPublicWaitlist;

// Public route skips ProviderLayout (which normally clears this), so drop the SSO shell now.
if (isPublic) {
  document.documentElement.classList.remove("dark-mode");
  document.getElementById("app-bootstrap-shell")?.remove();
}

function App() {

  if (!Sentry.isInitialized() && config.sentry?.dsn) {
    Sentry.init({
      dsn: config.sentry.dsn,
      release: `pathway-web@${config.version}`,
      dist: config.version,
      environment: config.env,
      _experiments: { enableLogs: true },
      integrations: [
        // "route" lifecycle (default) sends a session envelope on every history change (tab/URL updates)
        Sentry.browserSessionIntegration({ lifecycle: "page" }),
        Sentry.replayIntegration({
          maxReplayDuration: 30000,
          maskAllText: false,
          blockAllMedia: false,
        })
      ],
      sendDefaultPii: true,
      replaysSessionSampleRate: 0,
      replaysOnErrorSampleRate: config.sampleRate,
    });
  }

  // Public enrolment is fully separate: no ThemeProvider (no dark mode), its own light rsuite theme.
  if (isPublic) {
    return (
      <div id="app" className="app">
        <Suspense fallback={<Loader />}>
          <ErrorBoundary>
            <CustomProvider theme="light">
              {isPublicWaitlist ? <PublicWaitlistApp /> : <PublicEnrolmentApp />}
            </CustomProvider>
          </ErrorBoundary>
        </Suspense>
      </div>
    );
  }

  return (
    <ThemeProvider>
      <div id="app" className="app noselect">
        <Suspense fallback={<Loader />}>
          <ErrorBoundary>
            <Providers />
          </ErrorBoundary>
        </Suspense>
      </div>
    </ThemeProvider>
  );
}

export default App;
